/**
 * roles.ts — el rótulo de un rol, en un solo sitio.
 *
 * ▶ EXISTE PORQUE HABÍA DOS SITIOS Y LOS DOS ESTABAN MAL IGUAL.
 *
 * `Acceso.tsx` y `Usuarios.tsx` armaban la clave así:
 *
 *     t(`common.${rol === 'solo_lectura' ? 'readonly' : rol}`)
 *
 * Traducía `solo_lectura` y dejaba `contador` tal cual — pero la clave del
 * diccionario es `common.accountant`, no `common.contador`. Así que a
 * cualquiera con rol de contador la pantalla le enseñaba **`common.contador`**,
 * la llave cruda, donde iba su rol.
 *
 * No lo vio nadie por dos razones que conviene tener juntas:
 *
 *   1. el único usuario de la base es admin en las tres empresas, así que esa
 *      rama no se ejecutaba nunca — *un camino que nunca corrió no está
 *      probado, está escrito*, que es lo que el hub nos escribió en la `38`;
 *   2. **la prueba bilingüe no podía verlo.** Compara el español contra el
 *      inglés, y `common.contador` no está en ninguno de los dos: falta igual
 *      de los dos lados, así que la comparación sale verde. Es nuestra carta
 *      `37` con otro sujeto — el control valida que las dos copias sigan de
 *      acuerdo, no que la clave exista.
 *
 * Ahora hay un mapa explícito. Un rol nuevo sin rótulo no compila.
 */
import type { Clave } from './idioma.ts';

export type Rol = 'admin' | 'contador' | 'solo_lectura';

/**
 * Del rol como lo guarda la base al rótulo del diccionario.
 *
 * Escrito como mapa y no como plantilla: una plantilla convierte cualquier
 * texto en una clave que «parece» válida, y ahí se coló la de arriba.
 */
const ROTULOS: Readonly<Record<Rol, Clave>> = {
  admin: 'common.admin',
  contador: 'common.accountant',
  solo_lectura: 'common.readonly',
};

export const ROLES: readonly Rol[] = ['admin', 'contador', 'solo_lectura'];

export function rotuloDeRol(rol: string): Clave {
  // Un rol que la base devolviera y no estuviera acá enseñaría la llave. Se
  // cae a `admin` no: se devuelve una clave que existe y dice lo que pasa.
  return ROTULOS[rol as Rol] ?? 'common.unknownRole';
}
